import { useRef, useEffect, useState } from 'react';
import { Title } from 'react-admin';
import { Box, Typography } from '@mui/material';

const TABLEAU_URL =
  'https://10ay.online.tableau.com/t/orchestraaetc/views/Analyticstest/Dashboard1?:showAppBanner=false&:display_count=n&:showVizHome=n&:origin=viz_share_link';

const TABLEAU_JS_URL =
  'https://10ay.online.tableau.com/javascripts/api/viz_v1.js';

interface TableauViz {
  dispose?: () => void;
}

interface TableauApi {
  Viz: new (container: HTMLDivElement, url: string) => TableauViz;
}

type TableauWindow = Window & typeof globalThis & { tableau?: TableauApi };

const Analytics = () => {
  const ref = useRef<HTMLDivElement>(null);
  const vizRef = useRef<TableauViz | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    const container = ref.current;
    const tableauWindow = window as TableauWindow;
    const createViz = () => {
      if (active && container && tableauWindow.tableau && !vizRef.current) {
        vizRef.current = new tableauWindow.tableau.Viz(container, TABLEAU_URL);
      }
    };
    const handleError = () => {
      if (active) setError(true);
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TABLEAU_JS_URL}"]`,
    );
    const script = existing ?? document.createElement('script');
    const ownsScript = !existing;

    if (tableauWindow.tableau) {
      createViz();
    } else {
      script.addEventListener('load', createViz);
      script.addEventListener('error', handleError);
      if (ownsScript) {
        script.src = TABLEAU_JS_URL;
        document.head.appendChild(script);
      }
    }

    return () => {
      active = false;
      script.removeEventListener('load', createViz);
      script.removeEventListener('error', handleError);
      vizRef.current?.dispose?.();
      vizRef.current = null;
      container?.replaceChildren();
      if (ownsScript) script.remove();
    };
  }, []);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" pt={2}>
      <Title title="Analytics" />
      {error && (
        <Typography color="text.secondary" sx={{ mt: 4 }}>
          Unable to load Tableau analytics. Please check your network connection.
        </Typography>
      )}
      <div ref={ref} />
    </Box>
  );
};

export default Analytics;
